import { CardImageElement, PostReadingTime, PostStructuredData } from '@nucleus/post-theme-elements';
import { PostContentBlockWeb } from '@nucleus/types/web/sections/post-content';
import { ErrorBoundary, RichTextRegistryProvider } from '@nucleus/web-theme';
import { Text, nucleusClass } from '@nucleus/web-theme-elements';
import React from 'react';
import styled from 'styled-components';
import { Body } from '../components/Base';
import { BlockProps } from '../types/component';

type BlockPostContentProps = Extract<BlockProps, { block?: PostContentBlockWeb; sectionType?: 'postContent' }>;

export const BlockPostContent = (props: BlockPostContentProps): JSX.Element => {
  const post = props.block?.post;

  if (!post) {
    return <></>;
  }

  return (
    <ContentContainer className={nucleusClass('post-body-content')}>
      <ErrorBoundary>
        <React.Suspense>
          <PostStructuredData post={post} />
        </React.Suspense>
      </ErrorBoundary>
      <ContentWrapper>
        <ReadingTimeContainer>
          <Text.L4 className={nucleusClass('post-reading-time')} style={{ margin: '0 0 1.5em', opacity: 0.5 }}>
            <PostReadingTime post={post} />
          </Text.L4>
        </ReadingTimeContainer>
        {post.body !== undefined && (
          <ContentBody>
            <RichTextRegistryProvider registerElements={{ image: CardImageElement }} registerText={{}}>
              <Body nodes={post.body} />
            </RichTextRegistryProvider>
          </ContentBody>
        )}
      </ContentWrapper>
    </ContentContainer>
  );
};

const ContentContainer = styled.div`
  position: relative;
  padding-top: max(calc(var(--unit-length) * 2), 40px);
  padding-bottom: calc(var(--unit-length) * 2);
`;

const ContentWrapper = styled.div`
  max-width: calc(1080px + (var(--unit-length) * 2));
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: calc(var(--unit-length) * 1);
  padding-right: calc(var(--unit-length) * 1);
`;

const ReadingTimeContainer = styled.div`
  width: 100%;
  max-width: 800px;
  /* margin-bottom: calc(var(--unit-length) * 1.5); */
`;

const ContentBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  width: 100%;

  ${Body} {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: 100%;
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6,
  & p,
  & blockquote,
  & div[class*='theme-text-'] {
    width: 100%;
    max-width: 800px;
  }

  /* --- Per-font-style margins --- */

  & .${Text.ClassName.headline1} {
    margin: 0.5em 0 0.15em 0;
  }
  & .${Text.ClassName.headline2} {
    margin: 0.5em 0 0.2em 0;
  }
  & .${Text.ClassName.headline3} {
    margin: 0.5em 0 0.3em 0;
  }
  & .${Text.ClassName.headline4} {
    margin: 0.9em 0 0.2em 0;
  }
  & .${Text.ClassName.headline5} {
    margin: 1.2em 0 0.3em 0;
  }
  & .${Text.ClassName.headline6} {
    margin: 1.3em 0 0.3em 0;
  }

  & .${Text.ClassName.paragraph1} {
    margin-bottom: 1em;
  }
  & .${Text.ClassName.paragraph2} {
    margin-bottom: 1em;
  }
  & .${Text.ClassName.paragraph3} {
    margin-bottom: 1em;
  }
  & .${Text.ClassName.paragraph4} {
    margin-bottom: 1em;
  }

  & .${Text.ClassName.blockquote1} {
    margin: 1.5em 0 1.95em 0;
    padding-right: 2em;
  }
  & .${Text.ClassName.blockquote2} {
    margin: 1.5em 0;
  }

  & .${Text.ClassName.label1} {
    margin: 0 0 0.75em 0;
  }
  & .${Text.ClassName.label2} {
    margin: 0 0 0.75em 0;
  }
  & .${Text.ClassName.label3} {
    margin: 0 0 0.75em 0;
  }
  & .${Text.ClassName.label4} {
    margin: 0 0 0.75em 0;
  }
  & .${Text.ClassName.label5} {
    margin: 0 0 0.75em 0;
  }
  & .${Text.ClassName.label6} {
    margin: 0 0 0.75em 0;
  }

  & [class*='theme-text-label']:has(+ [class*='theme-text-headline']) {
    margin-bottom: 1em;
  }

  & [class*='theme-text-label'] + [class*='theme-text-headline'] {
    margin-top: 0;
  }

  /* --- Non-font-styled element margins --- */

  & ul,
  & ol {
    width: 100%;
    max-width: 800px;
    margin-top: 1em;
    margin-bottom: 1em;
    position: relative;
    padding-left: calc(4px + 12px + 1.5em);
  }

  & ul::before,
  & ol::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: var(--color-button-primary-background);
  }

  && li {
    margin: 0;
  }

  & hr {
    margin: 3.8em 0;
  }

  & [data-block-type='image'],
  & [data-block-type='video'] {
    margin: 1em 0;
  }

  & [data-block-type='card'] {
    width: 100%;
    margin-bottom: clamp(12px, 2.5vw, 20px);
  }

  & [data-block-type='button'] {
    width: 100%;
    max-width: 800px;
    margin: 1em 0;
  }

  & [data-node-type='link'] {
    color: var(--color-button-primary-background);
  }

  & > div > :first-child {
    margin-top: 0;
  }

  & > div > :last-child {
    margin-bottom: 0;
  }
`;
